import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { HomeTabsNavigatorParamList } from "@/navigators/HomeTabsNavigator/types";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";

import { useGetPackageNeedingAttention } from "../../hooks/useGetPackageNeedingAttention";
import { PackageNeedsAttentionBanner_customer$key } from "./__generated__/PackageNeedsAttentionBanner_customer.graphql";

type Props = {
  customerRef?: PackageNeedsAttentionBanner_customer$key | null;
};

export const PackageNeedsAttentionBanner = ({ customerRef }: Props) => {
  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const customer = useFragment(
    PackageNeedsAttentionBannerFragmentString,
    customerRef
  );

  const packageNeedingAttention = useGetPackageNeedingAttention(customer);

  if (!packageNeedingAttention) return null;

  const packageString =
    packageNeedingAttention.packageType === "MembershipPackage"
      ? t`Your membership`
      : t`One of your packages`;

  const messageMap = {
    DELINQUENT: t`${packageString} is delinquent. Please contact your club for assistance.`,
    NEEDS_PAYMENT: t`${packageString} requires a payment. Please complete the payment to avoid interruption of services.`,
    MISSING_PAYMENT_METHOD: t`${packageString} is missing a payment method. Please add a payment method to continue enjoying your services.`,
    EXPIRED_PAYMENT_METHOD: t` ${packageString} has an expired payment method. Please update your payment information to continue enjoying your services.`,
  };

  const actionButtonString = t`Manage ${
    packageNeedingAttention.packageType === "MembershipPackage"
      ? t`Membership`
      : t`Packages`
  }`;

  const goToProfile = () => {
    navigation.navigate("Profile");
  };

  return (
    <InfoBanner
      message={messageMap[packageNeedingAttention.type]}
      action={{
        label: actionButtonString,
        handler: goToProfile,
      }}
      colorTheme="ERROR"
    />
  );
};

const PackageNeedsAttentionBannerFragmentString = graphql`
  fragment PackageNeedsAttentionBanner_customer on Customer {
    id
    ...useGetPackageNeedingAttention_customer
  }
`;
